import type {
  AssetCondition,
  AssetStatus,
  AssigneeType,
  IntegrationStatus,
  LoginEventType,
  PersonType,
  RoomKind,
  SyncFrequency,
  SyncStatus,
  TenantRole,
} from './types';

export const ASSET_STATUS_LABEL: Record<AssetStatus, string> = {
  AVAILABLE: 'Available',
  CHECKED_OUT: 'Checked out',
  IN_REPAIR: 'In repair',
  LOST: 'Lost',
  STOLEN: 'Stolen',
  RETIRED: 'Retired',
  DISPOSED: 'Disposed',
};

export const CONDITION_LABEL: Record<AssetCondition, string> = {
  NEW: 'New',
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  DAMAGED: 'Damaged',
};

export const PERSON_TYPE_LABEL: Record<PersonType, string> = {
  STUDENT: 'Student',
  TEACHER: 'Teacher',
  STAFF: 'Staff',
  ALUMNI: 'Alumni',
  PAST_STUDENT: 'Past student',
  CANDIDATE: 'Candidate',
  OTHER: 'Other',
};

export const ROOM_KIND_LABEL: Record<RoomKind, string> = {
  CLASSROOM: 'Classroom',
  LAB: 'Lab',
  OFFICE: 'Office',
  LIBRARY: 'Library / media',
  STORAGE: 'Storage',
  COMMON: 'Common area',
  OTHER: 'Other',
};

export const ASSIGNEE_TYPE_LABEL: Record<AssigneeType, string> = {
  PERSON: 'Person',
  ROOM: 'Room',
  BUILDING: 'Building',
  OTHER: 'Other',
};

export const ROLE_LABEL: Record<TenantRole, string> = {
  ADMIN: 'Admin',
  TECHNICIAN: 'Technician',
  VIEWER: 'Viewer',
  REQUESTER: 'Requester',
};

export const ROLE_DESCRIPTION: Record<TenantRole, string> = {
  ADMIN: 'Everything, including integrations, settings, users, imports, and deletes',
  TECHNICIAN: 'Manage assets, people, locations, work orders, and check assets in and out',
  VIEWER: 'Read-only access to inventory, people, work orders, and reports',
  REQUESTER:
    'Teachers and staff who report device problems: their own and their rooms’ devices are listed, and any other device can be reported by asset tag. Linked to their person record by email.',
};

export const SYNC_FREQUENCY_LABEL: Record<SyncFrequency, string> = {
  MANUAL: 'Manual only',
  EVERY_MINUTE: 'Every minute',
  EVERY_5_MINUTES: 'Every 5 minutes',
  EVERY_15_MINUTES: 'Every 15 minutes',
  HOURLY: 'Every hour',
  EVERY_6_HOURS: 'Every 6 hours',
  DAILY: 'Once a day',
};

export const SYNC_STATUS_LABEL: Record<SyncStatus, string> = {
  QUEUED: 'Queued',
  RUNNING: 'Running',
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
};

export const INTEGRATION_STATUS_LABEL: Record<IntegrationStatus, string> = {
  NOT_CONFIGURED: 'Not connected',
  CONNECTED: 'Connected',
  ERROR: 'Needs attention',
};

export const LOGIN_EVENT_LABEL: Record<LoginEventType, string> = {
  LOGIN: 'Signed in',
  LOGOUT: 'Signed out',
  LOGIN_FAILURE: 'Sign-in failed',
  OTHER: 'Other',
};

const dateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
const relativeFormat = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
const moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const toDate = (value: string | Date) => (value instanceof Date ? value : new Date(value));

export function formatDate(value: string | Date | null | undefined) {
  return value ? dateFormat.format(toDate(value)) : '—';
}

/** For date-only values stored as UTC midnight (e.g. Google usage dates). */
export function formatUtcDate(value: string | null | undefined) {
  return value ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(value)) : '—';
}

export function formatDateTime(value: string | Date | null | undefined) {
  return value ? dateTimeFormat.format(toDate(value)) : '—';
}

/** When work is due: just the day when it's due by the end of the day, otherwise the day and time. */
export function formatDue(value: string | Date | null | undefined) {
  if (!value) return '—';
  const date = toDate(value);
  return date.getHours() === 23 && date.getMinutes() === 59 ? dateFormat.format(date) : dateTimeFormat.format(date);
}

export function formatRelative(value: string | Date | null | undefined) {
  if (!value) return '—';
  const diffSeconds = (toDate(value).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diffSeconds);
  if (abs < 45) return 'just now';
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['minute', 60],
    ['hour', 3600],
    ['day', 86400],
    ['month', 2_592_000],
    ['year', 31_536_000],
  ];
  let unit: Intl.RelativeTimeFormatUnit = 'minute';
  let size = 60;
  for (const [candidate, seconds] of units) {
    if (abs >= seconds) {
      unit = candidate;
      size = seconds;
    }
  }
  return relativeFormat.format(Math.round(diffSeconds / size), unit);
}

export function formatMinutes(minutes: number | null | undefined) {
  if (!minutes) return '0m';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

export function formatMoney(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return '—';
  return moneyFormat.format(Number(value));
}

export function formatNumber(value: number | null | undefined) {
  return (value ?? 0).toLocaleString('en-US');
}

export function personName(person: { firstName: string; lastName: string; preferredName?: string | null }) {
  return `${person.preferredName?.trim() || person.firstName} ${person.lastName}`.trim();
}

export function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function isOverdue(dueAt: string | null | undefined, checkedInAt?: string | null) {
  return !!dueAt && !checkedInAt && new Date(dueAt).getTime() < Date.now();
}

/** `YYYY-MM-DD` for <input type="date"> in the viewer's local time. */
export function toDateInput(value: string | Date | null | undefined) {
  if (!value) return '';
  const date = toDate(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Date-only fields (purchase date, warranty): store at noon UTC so the day never shifts. */
export function dateInputToIso(value: string) {
  return value ? `${value}T12:00:00.000Z` : null;
}

/** Due dates: end of the chosen day in the viewer's time zone. */
export function dueDateInputToIso(value: string) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 0).toISOString();
}

export function addDays(days: number, from = new Date()) {
  return new Date(from.getTime() + days * 86_400_000);
}

export function humanizeKey(key: string) {
  const words = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
